import React, { useEffect, useMemo, useState } from 'react';
import { Polygon, Tooltip } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { createFieldGroupAreas, groupStationsByArea, createAreaPolygon, getAreaLabel, parseLocationDescription } from '../../utils/areaOverlayUtils';
import './AreaOverlay.css';

const getCompactAreaDetails = (area) => {
  const parts = [];
  if (typeof area?.campoApertoCount === 'number') parts.push(`${area.campoApertoCount} CA`);
  if (typeof area?.retroModuloCount === 'number') parts.push(`${area.retroModuloCount} RM`);
  if (typeof area?.areaHa === 'number') parts.push(`${area.areaHa.toFixed(2)} ha`);
  return parts.join(' • ');
};

export function AreaOverlay({ 
  farm = null,
  stations, 
  visible = true, 
  enableTooltips = false, 
  tooltipPermanent = false,
  onAreaClick = null,
  selectedSections = [],
  selectAllSections = false,
  sections = [],
  groups = null,
  groupLevel = null,
}) {
  const { t } = useTranslation();
  const [hoveredAreaName, setHoveredAreaName] = useState(null);

  // When switching farms / station sets, clear any stale hover state so labels behave
  // consistently across all farms in Analytics.
  useEffect(() => {
    setHoveredAreaName(null);
  }, [farm?.id, stations, tooltipPermanent, groupLevel]);
  const areaGroups = useMemo(() => {
    if (groups && Array.isArray(groups)) return groups;
    if (!stations || stations.length === 0) return {};
    if (farm?.sections && farm.sections.length > 0) {
      const sectionLookup = new Map();
      farm.sections.forEach(section => {
        if (section.id) sectionLookup.set(section.id, section);
        if (section.name) sectionLookup.set(section.name, section);
      });

      const groups = {};
      stations.forEach(station => {
        const sectionKey = station.sectionId || station.locationDescription;
        const section = sectionLookup.get(sectionKey);
        if (!section) return;

        const groupKey = section.id || section.name;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            areaName: section.name || section.id || groupKey,
            field: 'Section',
            area: section.name || section.id || groupKey,
            stations: [],
            bounds: null,
            polygon: section.polygon,
            areaHa: section.areaHa,
          };
        }

        groups[groupKey].stations.push(station);
      });

      Object.values(groups).forEach(area => {
        area.campoApertoCount = area.stations.filter(s => s.sensorType === 'campo_aperto').length;
        area.retroModuloCount = area.stations.filter(s => s.sensorType === 'retro_modulo').length;
        area.totalCount = area.stations.length;
      });

      return groups;
    }
    // Prefer the real field polygon split into two groups (Section A / Section B)
    // when a farm polygon is available; fallback to station bounding-box grouping.
    if (farm?.polygon && Array.isArray(farm.polygon) && farm.polygon.length >= 3) {
      const groups = createFieldGroupAreas(farm.polygon, stations);
      if (groups) return groups;
    }
    return groupStationsByArea(stations);
  }, [stations, farm, groups]);

  // Create set of selected section IDs for quick lookup
  const selectedSectionIds = useMemo(() => {
    if (selectAllSections && sections.length > 0) {
      return new Set(sections.map(s => s.id));
    }
    return new Set(selectedSections);
  }, [selectedSections, selectAllSections, sections]);

  // Map area names to section IDs
  // Support multiple keys: section name (e.g., "Section A") and location description
  const areaNameToSectionId = useMemo(() => {
    const map = new Map();
    sections.forEach(section => {
      // Map by section name (e.g., "Section A")
      if (section.name) {
        map.set(section.name, section.id);
      }
      // Also map by locationDescription if available (e.g., "Section A - Campo Aperto")
      if (section.locationDescription) {
        // Parse the locationDescription to get the area name
        const parsed = parseLocationDescription(section.locationDescription);
        if (parsed.fullArea && !map.has(parsed.fullArea)) {
          map.set(parsed.fullArea, section.id);
        }
      }
    });
    return map;
  }, [sections]);

  const areaGroupList = Array.isArray(areaGroups) ? areaGroups : Object.values(areaGroups);

  if (!visible || areaGroupList.length === 0) {
    return null;
  }

  return (
    <>
      {areaGroupList.map((area, index) => {
        const polygonCoords = area.polygon ? area.polygon : createAreaPolygon(area.bounds);
        if (!polygonCoords) return null;

        // Always make interactive if onAreaClick is provided
        const isInteractive = onAreaClick !== null || enableTooltips;
        
        // Determine if this area is selected
        // If no `sections` are provided, treat overlays as always-selected (map view).
        const sectionIds = Array.isArray(area.sectionIds)
          ? area.sectionIds.filter(Boolean)
          : [];
        const mappedSectionId = sectionIds.length === 0 ? areaNameToSectionId.get(area.areaName) : null;
        const resolvedSectionIds = sectionIds.length > 0
          ? sectionIds
          : (mappedSectionId ? [mappedSectionId] : []);
        const isSelected = sections.length === 0
          ? true
          : (selectAllSections
            ? true
            : resolvedSectionIds.length > 0 && resolvedSectionIds.every(id => selectedSectionIds.has(id)));

        // Style based on selection state
        // Selected: green overlay | Deselected: gray overlay
        const pathOptions = isSelected ? {
          color: '#2D5016',        // Dark green border
          fillColor: '#4A7A2A',    // Green fill
          fillOpacity: tooltipPermanent ? 0.62 : 0.5, // Stronger on Analytics map
          weight: 3,               // Thicker border when selected
          opacity: 1
        } : {
          color: '#6B7280',        // Gray border
          fillColor: '#9CA3AF',    // Gray fill
          fillOpacity: tooltipPermanent ? 0.45 : 0.35, // Stronger on Analytics map
          weight: tooltipPermanent ? 2.5 : 2,          // Slightly thicker on Analytics map
          opacity: 0.7
        };

        // Click handler for polygon
        const handleClick = (e) => {
          if (onAreaClick) {
            // Stop propagation to prevent double-triggering
            if (e && e.originalEvent) {
              e.originalEvent.stopPropagation();
            }
            onAreaClick(area.areaName);
          }
        };

        const handleMouseOver = (e) => {
          if (tooltipPermanent) {
            setHoveredAreaName(area.areaName);
          }
          if (onAreaClick) {
            // Change cursor to pointer on hover
            e.target.setStyle({ cursor: 'pointer' });
          }
        };

        const handleMouseOut = (e) => {
          if (tooltipPermanent) {
            setHoveredAreaName(prev => (prev === area.areaName ? null : prev));
          }
          if (onAreaClick) {
            // Reset cursor
            e.target.setStyle({ cursor: '' });
          }
        };

        return (
          <Polygon
            key={`area-${index}-${area.areaName}`}
            positions={polygonCoords}
            pathOptions={pathOptions}
            interactive={isInteractive}
            eventHandlers={(onAreaClick || tooltipPermanent) ? {
              ...(onAreaClick ? { click: handleClick } : {}),
              mouseover: handleMouseOver,
              mouseout: handleMouseOut,
            } : undefined}
          >
            {enableTooltips && (
              <Tooltip
                className={[
                  'area-tooltip',
                  tooltipPermanent ? 'area-tooltip--permanent' : '',
                  isSelected ? 'area-tooltip--selected' : 'area-tooltip--unselected'
                ].filter(Boolean).join(' ')}
                permanent={tooltipPermanent}
                direction={tooltipPermanent ? 'center' : 'top'}
                offset={tooltipPermanent ? [0, 0] : [0, -10]}
              >
                <div className={[
                  'area-tooltip__content',
                  tooltipPermanent ? 'area-tooltip__content--permanent' : '',
                  isSelected ? 'area-tooltip__content--selected' : 'area-tooltip__content--unselected'
                ].filter(Boolean).join(' ')}>
                  <div className="area-tooltip__title">{area.areaName}</div>
                  {!tooltipPermanent && (
                    <div className="area-tooltip__stats">{getAreaLabel(area, t)}</div>
                  )}
                  {tooltipPermanent && hoveredAreaName === area.areaName && (
                    <div className="area-tooltip__stats">{getCompactAreaDetails(area)}</div>
                  )}
                </div>
              </Tooltip>
            )}
          </Polygon>
        );
      })}
    </>
  );
}
